/* eslint-disable jsx-a11y/media-has-caption */
import React, { useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Avatar,
  Chip,
} from "@mui/material";
import { Call as CallIcon, CallEnd as CallEndIcon } from "@mui/icons-material";
import { useWebRTC } from "src/context/chats/WebRTCContext";
import useCallTone from "src/hooks/useCallTone";

const CallControl: React.FC = () => {
  const {
    callState,
    activeCall,
    incomingQueue,
    acceptCall,
    rejectCall,
    terminateCall,
    remoteStream,
    formattedDuration,
    isStandalone,
  } = useWebRTC();
  const audioRef = useRef<HTMLAudioElement>(null);

  // 🔔 Play incoming ringtone in main tab so agent hears it immediately
  // Only plays for "incoming" — ringing/active tones are handled by the pop-out
  useCallTone(callState === "incoming" ? "incoming" : "idle");

  useEffect(() => {
    // Only play audio in the standalone window to avoid echo/doubling
    if (audioRef.current && remoteStream && isStandalone) {
      audioRef.current.srcObject = remoteStream;
    }
  }, [remoteStream, isStandalone]);

  if (callState === "idle") return null;

  // 🛡️ Multi-Call View: If we have multiple incoming calls, show all of them in a vertical column
  if (callState === "incoming" && incomingQueue.length > 0) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 80,
          right: 20,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          maxHeight: "calc(100vh - 120px)",
          overflowY: "auto",
          padding: "10px",
          pr: "20px", // space for scrollbar
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0, 0, 0, 0.1)",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        {incomingQueue.map((call) => (
          <Paper
            key={call.callId}
            elevation={6}
            sx={{
              p: 2,
              width: 280,
              borderRadius: 4,
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transition: "all 0.3s ease",
              position: "relative",
              "&:hover": { transform: "translateX(-5px)" },
            }}
          >
            <Chip
              label="WhatsApp"
              size="small"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                height: 18,
                fontSize: 10,
                color: "white",
                background: "#25D366",
              }}
            />
            <Avatar
              sx={{ width: 64, height: 64, mb: 1, border: "2px solid #25D366" }}
              src={call.customerPicture}
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {call.customerName || call.from || "WhatsApp User"}
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ mb: 1 }}>
              Incoming WhatsApp Call...
            </Typography>

            {!call.inboxId && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mb: 2, textAlign: "center", fontWeight: 500 }}
              >
                New Customer! <br />
                Claim this chat in "Pending Chats" first.
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 2 }}>
              <IconButton
                onClick={() => {
                  // 🛡️ Accept this specific call
                  acceptCall(call.callId);
                }}
                disabled={!call.inboxId}
                sx={{
                  bgcolor: call.inboxId ? "#25D366" : "#ccc",
                  color: "white",
                  "&:hover": {
                    bgcolor: call.inboxId ? "#128C7E" : "#ccc",
                  },
                  width: 48,
                  height: 48,
                }}
              >
                <CallIcon />
              </IconButton>
              <IconButton
                onClick={() => rejectCall()}
                sx={{
                  bgcolor: "#FF3B30",
                  color: "white",
                  "&:hover": { bgcolor: "#D70015" },
                  width: 48,
                  height: 48,
                }}
              >
                <CallEndIcon />
              </IconButton>
            </Box>
          </Paper>
        ))}
        <audio ref={audioRef} autoPlay muted={!isStandalone} />
      </Box>
    );
  }

  return (
    <Paper
      elevation={6}
      sx={{
        position: "fixed",
        top: 80,
        right: 40,
        zIndex: 9999,
        p: 2,
        width: 280,
        borderRadius: 4,
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Chip
        label="WhatsApp"
        size="small"
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          height: 18,
          fontSize: 10,
          color: "white",
          background: "#25D366",
        }}
      />
      <Avatar
        sx={{ width: 64, height: 64, mb: 1, border: "2px solid #25D366" }}
        src={activeCall?.customerPicture}
      />
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {activeCall?.customerName || activeCall?.from || "WhatsApp User"}
      </Typography>
      <Typography variant="caption" color="textSecondary" sx={{ mb: 1 }}>
        {(() => {
          if (callState === "ringing") return "Ringing (Pop-out)...";
          if (callState === "active" && !isStandalone)
            return `In Call (Pop-out) - ${formattedDuration}`;
          return formattedDuration;
        })()}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        <IconButton
          onClick={() => terminateCall()}
          sx={{
            bgcolor: "#FF3B30",
            color: "white",
            "&:hover": { bgcolor: "#D70015" },
            width: 48,
            height: 48,
          }}
        >
          <CallEndIcon />
        </IconButton>
        {!isStandalone && (
          <Typography
            variant="caption"
            sx={{
              cursor: "pointer",
              textDecoration: "underline",
              color: "primary.main",
            }}
            onClick={() => {
              const popup = window.open("", "WhatsAppCall");
              if (popup) popup.focus();
            }}
          >
            Bring to Front
          </Typography>
        )}
      </Box>
      <audio ref={audioRef} autoPlay muted={!isStandalone} />
    </Paper>
  );
};

export default CallControl;
